import { useState } from 'react';
import { Plus, Plane, CalendarOff } from 'lucide-react';
import {
  Skeleton,
  Badge,
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useBusinessTrips, type BusinessTripEntry } from '@/api/dashboard-scheduling';
import { WidgetCard } from './WidgetCard';

/**
 * Business trips & day-offs (mockup: Công tác / nghỉ phép). REAL data from
 * /dashboard/business_and_dayoffs, bucketed today / tomorrow. A "New" header
 * action opens a create Drawer (entry-point shell — full form wiring lives in
 * the Business module).
 */

function TripRow({ entry, index }: { entry: BusinessTripEntry; index: number }) {
  const isTrip = entry.business_type === 'business';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-3)',
        padding: 'var(--vn-space-2) var(--vn-space-3)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-muted)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--vn-radius-full)',
          background: 'var(--vn-bg-subtle)',
          /* icon color here uses solid tokens for background-adjacent intent icons */
          color: isTrip ? 'var(--vn-info-text)' : 'var(--vn-warning-text)',
          flexShrink: 0,
        }}
      >
        {isTrip ? <Plane size={14} /> : <CalendarOff size={14} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {index + 1}. {entry.employee.employee_name}
        </div>
        {entry.employee.department_name ? (
          <div
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-muted)',
              textTransform: 'capitalize',
            }}
          >
            {entry.employee.department_name.toLowerCase()}
          </div>
        ) : null}
      </div>
      <Badge tone={isTrip ? 'info' : 'warning'} appearance="subtle" size="sm">
        {isTrip ? 'Business' : 'Day-off'}
      </Badge>
    </div>
  );
}

function Bucket({ label, entries }: { label: string; entries: BusinessTripEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <span
        style={{
          fontSize: 'var(--vn-font-size-xs)',
          fontWeight: 'var(--vn-font-weight-semibold)',
          color: 'var(--vn-fg-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
        {entries.map((e, i) => (
          <TripRow key={e.employee.employee_id} entry={e} index={i} />
        ))}
      </div>
    </div>
  );
}

export function BusinessTripsWidget() {
  const { data, isLoading } = useBusinessTrips();
  const [open, setOpen] = useState(false);

  const today = data?.today ?? [];
  const tomorrow = data?.tomorrow ?? [];
  const isEmpty = today.length === 0 && tomorrow.length === 0;

  return (
    <WidgetCard
      title="Business Trips"
      description="Trips & day-offs"
      actions={
        <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} onClick={() => setOpen(true)}>
          New
        </Button>
      }
    >
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyState size="sm" title="Nothing scheduled" description="Business trips & day-offs will show here." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Bucket label="Today" entries={today} />
          <Bucket label="Tomorrow" entries={tomorrow} />
        </div>
      )}

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent side="right" size="md">
          <DrawerHeader>
            <DrawerTitle>New business entry</DrawerTitle>
            <DrawerDescription>Register a business trip or a day-off.</DrawerDescription>
          </DrawerHeader>
          <DrawerBody>
            <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Full registration is handled in the Business module. Continue there to submit a new
              trip or day-off request.
            </p>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </WidgetCard>
  );
}
